import { inject, Pipe, PipeTransform } from '@angular/core';
import { formatNumber } from '@angular/common';
import { BazisLocaleService } from '@bazis/shared/services/locale.service';

/**

 * Formats a number using the current app locale. Supports Angular number
 * format patterns and optional rounding direction (up/down/none).
 * Pipe is `pure: false` so it updates when the locale changes.
 *
 * How it works:
 * - Gets the active locale from `BazisLocaleService`.
 * - Optionally rounds the value based on the fraction digits extracted
 *   from the `format` string (e.g., `'1.2-2'` → `2` digits).
 * - Uses Angular's `formatNumber` to render the value for the locale.
 *
 * Dependencies:
 * - `BazisLocaleService` (via `inject()`): provides the current locale.
 *
 * Signature:
 * `transform(value: any, format: string, roundDirection?: 'up' | 'down' | 'none', locale?: string): string`
 *
 * Parameters:
 * - `value`: Number to format.
 * - `format`: Angular number format pattern, e.g., `'1.2-2'` (`minInteger.minFraction-maxFraction`).
 * - `roundDirection`: `'up' | 'down' | 'none'` (default `'none'`).
 *   When not `'none'`, the pipe rounds the value to the max fraction digits from `format`.
 * - `locale`: Locale code. Default: current locale from `BazisLocaleService`.
 *
 * Examples (Angular v20+):
 * ```html
 * <!-- Basic: cache with @let -->
 * @let price = (product.$snapshot.price | localNumber:'1.2-2');
 * {{ price }}
 *
 * <!-- Round up to the format's max fraction digits -->
 * @let up = (value | localNumber:'1.0-2':'up');
 * {{ up }}
 *
 * <!-- Round down -->
 * @let down = (value | localNumber:'1.0-2':'down');
 * {{ down }}
 *
 * <!-- Override locale explicitly (e.g., force English formatting) -->
 * @let enValue = (value | localNumber:'1.2-2':'none':'en');
 * {{ enValue }}
 *
 * <!-- In a list: guard not required for valid numbers, provide fallback if needed -->
 * @for (p of products; track p.id) {
 *   @let label = (p.$snapshot.amount | localNumber:'1.0-2');
 *   <div>{{ p.$snapshot.name }} — {{ label || '-' }}</div>
 * }
 * ```
 */
@Pipe({
    name: 'localNumber',
    pure: false,
    standalone: true,
})
export class LocalNumberPipe implements PipeTransform {
    protected localeService = inject(BazisLocaleService);

    /**
     * Format number for the locale, with optional rounding.
     *
     * @param value - Input number.
     * @param format - Angular number pattern like '1.2-2'.
     * @param roundDirection - 'up' | 'down' | 'none' (default 'none').
     * @param locale - Locale code (defaults to current locale).
     */
    transform(
        value: any,
        format: string,
        roundDirection: 'up' | 'down' | 'none' = 'none',
        locale = this.localeService.locale,
    ) {
        const valueToFormat =
            roundDirection === 'none'
                ? value
                : this.getNumberWithFixedDigits(value, format, roundDirection);
        return formatNumber(valueToFormat, locale, format);
    }

    protected getNumberWithFixedDigits(value, format, roundDirection: 'up' | 'down') {
        /**
         * Round a number to the max fraction digits from the format.
         *
         * @param value Input number to round.
         * @param format Angular number pattern, e.g. '1.0-2'.
         * @param roundDirection 'up' uses Math.ceil, 'down' uses Math.floor.
         * @returns Rounded number.
         */
        const formatData = format.split('-');
        const digits = formatData.length > 1 ? +formatData[1] : 0;

        const roundFn = roundDirection === 'down' ? Math.floor : Math.ceil;
        return roundFn(value * 10 ** digits) / 10 ** digits;
    }
}
